import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, RefreshCw, Loader2, Printer, Coins, Cookie, ShoppingBag, Package } from "lucide-react";
import { fetchCommissions } from "@/lib/api";

// Use LOCAL calendar dates so filters align with orders timestamped in the
// user's local time (BST etc.). UTC-based dates cause off-by-one gaps around
// midnight and hide fresh eftpos/walk-in orders.
const p2 = (n) => String(n).padStart(2, "0");
const localISO = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const monthStartISO = () => {
  const d = new Date();
  d.setDate(1);
  return localISO(d);
};
const todayISO = () => localISO(new Date());
const fmt = (sym, n) => `${sym}${Number(n || 0).toFixed(2)}`;

const Field = ({ label, children, testId }) => (
  <div className="flex flex-col gap-1.5" data-testid={testId}>
    <label className="label-uppercase">{label}</label>
    {children}
  </div>
);

export const CommissionsModal = ({ onClose }) => {
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchCommissions({ date_from: dateFrom, date_to: dateTo });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load commissions.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const sym = data?.currency_symbol || "£";
  const rates = data?.rates || { dessert: 0.5, bucket: 4.0, bucket_family: 2.5, other: 1.0 };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start lg:items-center justify-center p-4 bg-black/60 print:bg-transparent print:p-0 print:block print:static overflow-y-auto"
      onClick={onClose}
      data-testid="commissions-modal"
    >
      <div
        className="brutal-card w-full max-w-5xl my-6 print:my-0 print:max-w-none print:shadow-none print:border-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between border-b-2 border-dotr-ink p-4 md:p-5 sticky top-0 bg-white z-10 flex-wrap gap-3 no-print">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted flex items-center gap-2">
              <Coins size={11} /> Partner Commissions
            </div>
            <div className="font-heading font-semibold text-xl md:text-2xl uppercase tracking-tight">
              Commissions Dashboard
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={load}
              data-testid="btn-commissions-refresh"
              className="brutal-btn-ghost px-3 py-2 text-xs flex items-center gap-2"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="btn-commissions-print"
              className="brutal-btn px-3 py-2 text-xs flex items-center gap-2"
            >
              <Printer size={14} /> <span className="hidden sm:inline">Print</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="btn-commissions-close"
              className="brutal-btn-ghost px-3 py-2 text-xs"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6 lg:p-8" data-testid="commissions-body">
          {/* Filters */}
          <div className="brutal-card p-4 mb-6 no-print">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="From" testId="commissions-date-from-field">
                <input
                  type="date"
                  data-testid="commissions-date-from"
                  className="brutal-input h-11 px-3 w-full font-body"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Field>
              <Field label="To" testId="commissions-date-to-field">
                <input
                  type="date"
                  data-testid="commissions-date-to"
                  className="brutal-input h-11 px-3 w-full font-body"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Field>
              <div className="flex items-end gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => { setDateFrom(monthStartISO()); setDateTo(todayISO()); }}
                  className="brutal-btn-ghost px-3 py-2 text-xs flex-1 md:flex-none"
                  data-testid="commissions-quick-month"
                >
                  This Month
                </button>
                <button
                  type="button"
                  onClick={() => { setDateFrom(todayISO()); setDateTo(todayISO()); }}
                  className="brutal-btn-ghost px-3 py-2 text-xs flex-1 md:flex-none"
                  data-testid="commissions-quick-today"
                >
                  Today
                </button>
              </div>
            </div>
            <div className="mt-3 font-mono text-[11px] text-dotr-inkmuted leading-snug">
              Rates: {fmt(sym, rates.other)}/item · {fmt(sym, rates.dessert)}/dessert · {fmt(sym, rates.bucket)}/bucket · {fmt(sym, rates.bucket_family)}/family bucket. Partner spokes only:{" "}
              <span className="font-bold text-dotr-ink">Aylesbury · Whitehouse · Hatfield</span>.
            </div>
          </div>

          {/* Receipt-style header (visible always; cleaner on print) */}
          <div className="mb-6 hidden print:block">
            <div className="text-center">
              <div className="font-heading text-2xl font-bold uppercase">Dum on the Run<sup className="text-sm ml-0.5">®</sup></div>
              <div className="font-mono text-sm uppercase tracking-widest mt-1">Partner Commission Statement</div>
              <div className="font-mono text-xs mt-1">
                {dateFrom} → {dateTo}
              </div>
            </div>
          </div>

          {!data ? (
            <div className="brutal-card p-10 text-center" data-testid="commissions-loading">
              <Loader2 className="animate-spin inline-block" />
              <div className="font-mono text-xs mt-2 uppercase tracking-widest text-dotr-inkmuted">Loading commissions…</div>
            </div>
          ) : (
            <>
              {/* Spoke breakdown cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="commissions-spokes">
                {data.spokes.map((s) => (
                  <div
                    key={s.spoke}
                    className="brutal-card p-5 flex flex-col"
                    data-testid={`commissions-spoke-${s.spoke.toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between border-b-2 border-dotr-ink pb-3 mb-3">
                      <span className="font-heading font-semibold text-xl uppercase tracking-tight">{s.spoke}</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">
                        {s.orders} orders
                      </span>
                    </div>

                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 font-mono text-xs">
                          <ShoppingBag size={12} /> Items × {fmt(sym, rates.other)}
                        </span>
                        <span className="font-mono text-sm">
                          {s.other_items} × {fmt(sym, rates.other)} = <span className="font-bold">{fmt(sym, s.other_commission)}</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 font-mono text-xs">
                          <Cookie size={12} /> Desserts × {fmt(sym, rates.dessert)}
                        </span>
                        <span className="font-mono text-sm">
                          {s.dessert_items} × {fmt(sym, rates.dessert)} = <span className="font-bold">{fmt(sym, s.dessert_commission)}</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 font-mono text-xs">
                          <Package size={12} /> Buckets × {fmt(sym, rates.bucket)}
                        </span>
                        <span className="font-mono text-sm">
                          {s.bucket_items || 0} × {fmt(sym, rates.bucket)} = <span className="font-bold">{fmt(sym, s.bucket_commission || 0)}</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 font-mono text-xs">
                          <Package size={12} /> Family buckets × {fmt(sym, rates.bucket_family)}
                        </span>
                        <span className="font-mono text-sm">
                          {s.family_bucket_items || 0} × {fmt(sym, rates.bucket_family)} = <span className="font-bold">{fmt(sym, s.family_bucket_commission || 0)}</span>
                        </span>
                      </div>
                      <div className="border-t border-dashed border-dotr-ink/30 pt-2 mt-2 flex items-center justify-between font-mono text-xs">
                        <span className="text-dotr-inkmuted uppercase tracking-widest">Gross revenue</span>
                        <span>{fmt(sym, s.gross_revenue)}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t-2 border-dotr-ink flex items-center justify-between">
                      <span className="label-uppercase">Commission</span>
                      <span
                        className="font-heading font-bold text-3xl text-dotr-primary tracking-tighter"
                        data-testid={`commissions-spoke-${s.spoke.toLowerCase()}-total`}
                      >
                        {fmt(sym, s.total_commission)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Detailed table */}
              <div className="brutal-card overflow-hidden mb-6">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse min-w-[880px]" data-testid="commissions-table">
                  <thead>
                    <tr className="bg-dotr-muted border-b-2 border-dotr-ink">
                      {[
                        "Spoke",
                        "Orders",
                        "Items",
                        "Desserts",
                        "Buckets",
                        "Family",
                        `Items @ ${fmt(sym, rates.other)}`,
                        `Desserts @ ${fmt(sym, rates.dessert)}`,
                        `Buckets @ ${fmt(sym, rates.bucket)}`,
                        `Family @ ${fmt(sym, rates.bucket_family)}`,
                        "Commission",
                      ].map((h) => (
                        <th key={h} className="text-left p-3 font-heading text-[11px] uppercase tracking-[0.12em] border-r border-dotr-ink last:border-r-0">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.spokes.map((s) => (
                      <tr key={s.spoke} className="border-b border-dotr-ink/30">
                        <td className="p-3 font-heading font-semibold uppercase border-r border-dotr-ink/30">{s.spoke}</td>
                        <td className="p-3 font-mono border-r border-dotr-ink/30">{s.orders}</td>
                        <td className="p-3 font-mono border-r border-dotr-ink/30">{s.other_items}</td>
                        <td className="p-3 font-mono border-r border-dotr-ink/30">{s.dessert_items}</td>
                        <td className="p-3 font-mono border-r border-dotr-ink/30">{s.bucket_items || 0}</td>
                        <td className="p-3 font-mono border-r border-dotr-ink/30">{s.family_bucket_items || 0}</td>
                        <td className="p-3 font-mono border-r border-dotr-ink/30">{fmt(sym, s.other_commission)}</td>
                        <td className="p-3 font-mono border-r border-dotr-ink/30">{fmt(sym, s.dessert_commission)}</td>
                        <td className="p-3 font-mono border-r border-dotr-ink/30">{fmt(sym, s.bucket_commission || 0)}</td>
                        <td className="p-3 font-mono border-r border-dotr-ink/30">{fmt(sym, s.family_bucket_commission || 0)}</td>
                        <td className="p-3 font-heading font-bold text-dotr-primary">{fmt(sym, s.total_commission)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-dotr-ink text-white">
                      <td className="p-3 font-heading uppercase">Total</td>
                      <td className="p-3 font-mono" colSpan={8}></td>
                      <td className="p-3 font-mono text-right uppercase text-[10px] tracking-widest">Grand total</td>
                      <td className="p-3 font-heading font-bold text-xl" data-testid="commissions-grand-total">
                        {fmt(sym, data.grand_total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
                </div>
              </div>

              <div className="font-mono text-[11px] text-dotr-inkmuted leading-relaxed">
                Excludes non-partner spokes (Bletchley, Glebefarm). All amounts in {data.currency || "GBP"}.
                Commissions are calculated on item quantity, not on revenue.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
